import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { signalRService } from '@services/signalr.service'
import { sessionKeys } from './useSession'

/**
 * Listen for real-time session activity via SignalR notification hub.
 * On VoteCast: invalidate progress query to fetch accurate in-memory counts.
 * On VotingResolved: immediately refetch session state for the new node.
 */
export function useSessionSignalR(sessionId: string | null) {
  const queryClient = useQueryClient()
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  useEffect(() => {
    const connection = signalRService.getNotificationConnection()
    if (!connection || !sessionId) return

    const handler = (event: any) => {
      const currentSessionId = sessionIdRef.current
      if (!currentSessionId) return

      const type = event?.Type ?? event?.type
      const data = event?.Data ?? event?.data
      if (!type || !data) return

      if (type === 'VoteCast') {
        const roundId = data.RoundId ?? data.roundId

        // SSE VoteCast payload counts are unreliable during in-memory voting rounds.
        // Fetch accurate progress from the /progress endpoint instead.
        if (roundId) {
          queryClient.invalidateQueries({ queryKey: sessionKeys.voting(currentSessionId, roundId) })
        }
      } else if (type === 'VotingResolved') {
        const roundId = data.RoundId ?? data.roundId

        // Voting completed — immediately refetch session state for the new node
        queryClient.invalidateQueries({ queryKey: sessionKeys.state(currentSessionId) })
        queryClient.invalidateQueries({ queryKey: sessionKeys.journal(currentSessionId) })

        // Also invalidate voting progress query
        if (roundId) {
          queryClient.invalidateQueries({ queryKey: sessionKeys.voting(currentSessionId, roundId) })
        }
      }
    }

    connection.on('receivenotification', handler)

    return () => {
      connection.off('receivenotification', handler)
    }
  }, [sessionId, queryClient])
}
